import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@ortho-next/auth-core';
import { map } from 'rxjs';
import { APP_CONFIG } from '../../core';
import { Guide, GuideService, UserService } from '../../services';
import { BaseComponent, ConfirmationComponent, HelpComponent, LanguageSelectionComponent } from '../../shared';


/**
 * Horizontal bar on top
 */
@Component({
	selector: 'navbar',
	templateUrl: './navbar.component.html',
	imports: [
		NgbDropdownModule,
		LanguageSelectionComponent
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class NavbarComponent extends BaseComponent {

	private readonly modalSrv = inject(NgbModal);
	private readonly config = inject(APP_CONFIG);
	private readonly userSrv = inject(UserService);
	private readonly authSrv = inject(AuthService);
	private readonly guideSrv = inject(GuideService);


	guideList: Guide[] = [];
	isMyhexplan = toSignal(this.userSrv.isMyhexplan(), { initialValue: false });
	isPlatformDemo = toSignal(this.userSrv.isPlatformDemo(), { initialValue: false });
	username = toSignal(this.userSrv.getUserName().pipe(map(name => `${name.firstName} ${name.lastName}`)), { initialValue: '' });

	readonly myhexplanUrl = this.config.tspSite;
	readonly eifuSiteUrl = this.config.eifuSite;
	readonly customerCareEmail = this.config.customerCareEmail;

	private readonly hasToAcceptLegalDocs = toSignal(this.userSrv.hasToAcceptLegalDocs(), { initialValue: false });
	private readonly checkLegalTermsAcceptance = effect(() => {
		if (this.hasToAcceptLegalDocs()) globalThis.location.href = this.config.legalTermsAcceptSite;
	});

	private readonly hasProductsAvailable = toSignal(this.userSrv.hasProductsAvailable(), { initialValue: true });
	private readonly checkProductsAvailability = effect(() => {
		if (!this.hasProductsAvailable()) this.openProductNotAvailableModal();
	});

	private openProductNotAvailableModal(): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().PRODUCT_NOT_AVAILABLE_MODAL_TITLE,
			message: this.labels().PRODUCT_NOT_AVAILABLE_MODAL_MESSAGE,
			confirmBtn: this.labels().PRODUCT_NOT_AVAILABLE_MODAL_CONFIRM,
			isCancelBtn: false
		};
		modalRef.result.then(() => this.authSrv.singleSignOut()).catch(error => false);
	}

	/**
	 * Get Guide list when open dropdown the first time.
	 */
	getGuideList(open: boolean): void {
		if (!!open && (!this.guideList || this.guideList.length === 0)) {
			this.guideSrv.getGuideList().subscribe(list => this.guideList = list);
		}
	}

	/**
	 * Open guide page.
	 */
	openGuide(path: string): void {
		const guideUrl = this.guideSrv.getGuideUrl(path);
		window.open(guideUrl, '_blank');
	}

	/**
	 * Open modal of help info.
	 */
	openHelpModal(): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.modalSrv.open(HelpComponent, { backdropClass: 'wrapper-modal-help', windowClass: 'wrapper-modal-help' });
		}
	}

}
